'use client'

import React, { createContext, ReactNode, useContext, useMemo } from 'react'

import type { HolidayCollection, HolidayPackage } from '@/src/types/holidays'

interface HolidayContextType {
    /** Set on a package detail route. */
    packageData: HolidayPackage | null
    /** Set on a collection listing route. */
    collectionData: HolidayCollection | null
    locale: string
    /** Market the page was served for (e.g. AE, FAE); client calls to the holidays API reuse it. */
    domainCountry: string
}

const initialState: HolidayContextType = {
    packageData: null,
    collectionData: null,
    locale: 'en',
    domainCountry: ''
}

const HolidayContext = createContext<HolidayContextType>(initialState)

interface HolidayProviderProps {
    children: ReactNode
    locale: string
    domainCountry: string
    packageData?: HolidayPackage | null
    collectionData?: HolidayCollection | null
}

export const HolidayProvider: React.FC<HolidayProviderProps> = ({
    children,
    locale,
    domainCountry,
    packageData = null,
    collectionData = null
}) => {
    const value = useMemo(
        () => ({ packageData, collectionData, locale, domainCountry }),
        [packageData, collectionData, locale, domainCountry]
    )

    return <HolidayContext.Provider value={value}>{children}</HolidayContext.Provider>
}

export const useHolidayContext = () => useContext(HolidayContext)

export { HolidayContext }
